<script setup lang="ts">
    // @ts-nocheck
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    // TODO Check av ändringar av value i input kommer behöva justeras för FormKit

    import KButton from "@/components/Button.vue";
    import { replaceSeparator } from "@/components/forms/inputs/Number/numberUtils";
    // For Pycharm users
    // noinspection ES6UnusedImports
    import {
        createNumberValidation,
        getMaxDecimals,
        getNumberConstraint,
        NumberValidationRule,
        validateDecimals,
        validateMinMax,
        validateNumber,
    } from "@/components/forms/inputs/Number/numberValidation";
    import { useValidation } from "@/components/forms/reusables/useValidation";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import KSuffix from "@/components/forms/subComponents/Suffix/KSuffix.vue";
    import { useStore } from "@/store";
    import { computed, getCurrentInstance, inject, ref, toRefs, watch } from "vue";

    const store = useStore();

    // TODO Använd CommonProps i types i Vue3
    interface Props {
        name: string;
        label?: string;
        /**
         * Skicka inte med number! Vissa inbyggda valideringsregler i Formulate för nummerfält gäller inte. Se nedan:
         * Max (maximum):
         *   maxvalue:256
         * Min (minimum):
         *   minvalue:10
         * Decimals (antal decimaler. Default 0):
         *   decimals:2
         *
         * min och max kommer automatiskt konverteras till minvalue och maxvalue, men det är för legacy purpose och
         * använd hellre maxvalue/minvalue direkt
         */
        // validation! debuggat NumberValidationRule inte funkar med något annat dvs NumberValidationRule | string. Så därför användar vi any för validation
        validation?: any;
        type?: never;
        /**
         * Max värde. Sätts hellre via validation.
         */
        maxValue?: number;
        /**
         * Min värde. Sätts hellre via validation.
         */
        minValue?: number;
        validationName?: string;
        disabled?: boolean;
        /**
         * Mängd som ska ökas/minskas med via plus/minus
         */
        step?: number;
        readonly?: boolean;
        /**
         * KSuffix.vue prop Text som renderas under input
         */
        suffixText?: string;
        /**
         * KSuffix.vue prop Ikon som renderas under input
         */
        suffixIcon?: string;
        /**
         * KSuffix.vue prop Typ av subtitle
         */
        suffixType?: string;
        ignoreEmptyData?: boolean;
        /**
         * Visa asterisk på label för att markera att fältet är obligatoriskt, utan att lägga till required-validering.
         */
        showAsRequired?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        validation: "" as NumberValidationRule,
        step: 1,
        ignoreEmptyData: false,
    });

    const { validation, maxValue, minValue, disabled, step, readonly, name, suffixText, ignoreEmptyData } =
        toRefs(props);

    const instance = getCurrentInstance();
    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    }>("configNames");

    /**
     * Repeaters kan skapa nya rader efter att formulärets data redan hunnit läsas in (t.ex vid redigering av
     * befintlig data), och då hinner KNumbers eget v-model (som alltid är satt, oavsett om något värde faktiskt
     * finns) skriva över det redan inlästa värdet med tomt innan vi själva fått chansen att läsa in det.
     *
     * Formulate exponerar det värde den redan registrerat för fältet via injicerade getGroupValues (i en
     * repeater-rad) eller getFormValues (direkt i ett formulär). Genom att läsa värdet härifrån slipper vi race:et.
     */
    const getGroupValues = inject<(callerCmp: unknown) => Record<string, unknown>>("getGroupValues", undefined);
    const getFormValues = inject<(callerCmp: unknown) => Record<string, unknown>>("getFormValues", undefined);

    const formulateValue = computed<string | number | null>(() => {
        const getValues = getGroupValues ?? getFormValues;

        return getValues ? ((getValues(instance?.proxy)?.[name.value] as string | number | null) ?? null) : null;
    });

    /**
     * Fältets visade värde. Bindningen är dubbelriktad, så det som skrivs här når också Formulate - poängen är
     * vägen dit: via komponentens egen bindning, inte genom en skrivning till inputRef.value.context.model.
     *
     * Den skillnaden är hela skälet till att fältet har kvar sin v-model. En skrivning direkt till context.model
     * inifrån input-hanteraren återinträder i Formulates pågående utdelning av formulärets värde till fälten, så
     * konverteringen skrivs över av samma punktvärde igen och konverteras på nytt - en synkron kedja utan slut
     * som låser webbläsarens tråd. Se KNumberLoop.spec.js.
     *
     * Det är också därför KNumber inte kan följa KTexts mönster (inget eget v-model, värdet speglas via @input):
     * KText visar värdet som det är, medan KNumber formaterar om det och därför måste äga det visade värdet.
     *
     * Startvärdet konverteras redan här, inte bara i watchern nedan. En repeater-rad som skapas efter inläsningen
     * har sitt värde registrerat direkt, så formulateValue ändras aldrig och watchern får inget att reagera på.
     */
    const inputValue = ref(replaceSeparator({ value: formulateValue.value }));
    const modifiedDisabled = ref(disabled?.value);
    const modifiedReadonly = ref(readonly?.value);
    const inputRef = ref();
    const modifiedSuffix = ref(suffixText?.value);

    const { modifiedValidation, expose: useValidationExpose } = useValidation({
        name: name.value,
        validation: createNumberValidation({
            baseValidation: validation?.value,
            min: minValue?.value,
            max: maxValue?.value,
        }),
        moduleName: configNames?.moduleName,
        subModuleName: configNames?.subModuleName,
    });

    const setDisabled = ({ value }: { value: boolean }) => {
        modifiedDisabled.value = value;
    };

    const setReadonly = ({ value }: { value: boolean }) => {
        modifiedReadonly.value = value;
    };

    const setSuffix = ({ value }: { value: string | null }) => {
        modifiedSuffix.value = value;
    };

    defineExpose({
        setDisabled,
        /** Används av convertFormData i KForm för att avgöra hur datan ska konverteras innan det skickas till BE */
        kType: "number",
        setReadonly,
        setSuffix,
        ignoreEmptyData: () => ignoreEmptyData.value,
        ...useValidationExpose,
    });

    /**
     * Max antal decimaler i värdet
     */
    const maxDecimals = computed<number | null>(() =>
        getMaxDecimals({
            validation: modifiedValidation?.value,
        })
    );
    /**
     * Minimum värde
     */
    const minimum = computed<number | null>(() =>
        getNumberConstraint({
            validation: modifiedValidation?.value,
            type: "min",
        })
    );
    /**
     * Maximum värde
     */
    const maximum = computed<number | null>(() =>
        getNumberConstraint({
            validation: modifiedValidation?.value,
            type: "max",
        })
    );

    const changeValue = ({ increase = true }: { increase?: boolean }) => {
        const number = Number(replaceSeparator({ value: inputValue.value, toComma: false }));

        // Konverterar step till int om maxDecimals är 0 och inskickade step är decimalt värde för att undvika misstag
        const modifiedStep = maxDecimals.value === 0 && step.value % 1 > 0 ? 1 : step.value;

        if (!isNaN(number)) {
            inputValue.value = (increase ? Number(number) + modifiedStep : Number(number) - modifiedStep)
                .toFixed(maxDecimals.value ?? 0)
                .toString();
        } else {
            inputValue.value = increase ? modifiedStep.toString() : "0";
        }
    };

    const shouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            ...(configNames ?? {}),
            name: name.value,
        })
    );

    watch(inputValue, (newValue?: string | number) => {
        /**
         * Rensar errors satta på formulärnivå vid ändring av värde i fältet
         * Errors satta på formuläret räknas inte som valideringsfel och behöver rensas manuellt
         */
        if (inputRef.value?.explicitErrors?.length) {
            inputRef.value.setErrors({});
        }

        const displayValue = replaceSeparator({ value: newValue });

        if (displayValue !== newValue) {
            inputValue.value = displayValue;
        }
    });

    /**
     * Repeaters kan skapa nya rader efter att formulärdata redan lästs in, så det värde Formulate registrerat
     * måste synkas om till inputValue för att inte visa tomt/gammalt värde eller trigga required-fel.
     *
     * Jämförelsen görs mot det konverterade värdet och som text: fältet visar komma medan Formulate kan ha punkt,
     * och talet 6 är aldrig strikt lika med strängen "6".
     */
    watch(formulateValue, (newValue) => {
        const displayValue = replaceSeparator({ value: newValue });

        if (String(displayValue) === String(inputValue.value)) {
            return;
        }

        inputValue.value = displayValue;
    });
</script>

<template>
    <!-- TODO Ända till formkit number, om det finns -->
    <FormulateInput
        v-if="shouldBeVisible"
        v-bind="{ ...$attrs, ...$props, ...$slots }"
        ref="inputRef"
        v-model="inputValue"
        error-behavior="value"
        :disabled="modifiedDisabled"
        :validation-name="validationName ?? label"
        :validation="modifiedValidation"
        :validation-rules="{
            knumber: validateNumber,
            decimals: ({ value }) => validateDecimals({ value, max: maxDecimals }),
            minvalue: ({ value }) => validateMinMax({ value, constraint: minimum, type: 'min' }),
            maxvalue: ({ value }) => validateMinMax({ value, constraint: maximum, type: 'max' }),
        }"
        :validation-messages="{
            knumber: ({ name }) => `${name} är inte ett giltigt nummer`,
            decimals: ({ name }) =>
                `${name} ${maxDecimals > 0 ? `får max ha ${maxDecimals} decimaler` : 'får inte ha några decimaler'}`,
            minvalue: ({ name }) => `${name} måste vara minst ${minimum}`,
            maxvalue: ({ name }) => `${name} får vara max ${maximum}`,
        }"
        type="text"
        class="k-number"
        :readonly="modifiedReadonly"
    >
        <template #label>
            <KLabel v-if="label" :validation="modifiedValidation" :content="label" :showAsRequired="showAsRequired" />
        </template>
        <template #suffix>
            <div class="number-buttons">
                <KButton :disabled="modifiedDisabled" type="button" @click="changeValue" variant="none" tabindex="-1">
                    <span>+</span>
                </KButton>
                <KButton
                    :disabled="modifiedDisabled"
                    type="button"
                    @click="changeValue({ increase: false })"
                    variant="none"
                    tabindex="-1"
                >
                    <span>-</span>
                </KButton>
            </div>
        </template>
        <template #help>
            <KSuffix class="span-all" :subText="modifiedSuffix" :subIcon="suffixIcon" :subType="suffixType" />
        </template>
    </FormulateInput>
</template>

<style scoped lang="scss">
    @import "@/style/variables";
    @import "@/style/form";

    .k-number {
        ::v-deep .formulate-input-element {
            position: relative;
            height: calc($form-input-height-default + ($input-border-width * 2));

            input {
                height: 100%;
            }
        }

        .number-buttons {
            position: absolute;
            right: 0;
            top: 0;
            height: 100%;
            display: grid;
            grid-template-rows: repeat(2, 1fr);
            border-left: $input-border;

            .button {
                color: $color-button-standard-active;
                height: 100%;
                width: 1.5rem;
                min-width: unset;
                padding: 0;
                margin: 0;
                box-sizing: border-box;
                border-right: $input-border;

                &:first-child {
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;
                    border-top-right-radius: $border-radius;
                    border-top: $input-border;
                }

                &:last-child {
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                    border-top-right-radius: 0;
                    border-bottom-right-radius: $border-radius;
                    border-top: $input-border;
                    border-bottom: $input-border;
                }
            }
        }

        &[data-is-showing-errors="true"]:not(:has(input:focus)) {
            .number-buttons {
                height: calc(100% - $input-border-error-height);

                .button {
                    &:last-child {
                        border-bottom: none;
                        border-bottom-right-radius: 0;
                    }
                }
            }
        }
    }
</style>
